import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import FavoriteIcon from '@mui/icons-material/Favorite';
import TodayIcon from '@mui/icons-material/Today';
import ListIcon from '@mui/icons-material/List';
import TelegramAnalytics from '@telegram-apps/analytics';

import HomeTab from './components/HomeTab';
import HistoryTab from './components/HistoryTab';
import FavoritesTab from './components/FavoritesTab';

import './App.css';
import { UserType, TelegramThemeParams } from './common/commonTypes';

import { decodeTelegramUrlParams } from './utils/decodeTelegramUrlParams';
import { login } from './api';
import { AppBar, Drawer, IconButton, Toolbar } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DrawerContent from './components/DrawerContent';
import InstallAppModal from './components/InstallAppModal';
import { CALPAL_THEME } from './common/colors';
import colors from 'tailwindcss/colors';
import CircularProgress from '@mui/material/CircularProgress';
import SubscribeModal from './components/DrawerContent/SubscribeModal';
import { isUserUnsubscribed } from './utils/helpers';
import ContactModal from './components/DrawerContent/ContactModal';
import Snackbar from '@mui/material/Snackbar';
import { useSubscribeModal } from './SubscribeModalContext';

const env = process.env.NODE_ENV;

const App: React.FC = () => {
    const [user, setUser] = useState<UserType | null>(null);

    const [drawerOpen, setDrawerOpen] = React.useState(false);

    const {
        isOpen: showSubscribeModal,
        open: openSubscribe,
        close: closeSubscribe,
    } = useSubscribeModal();

    const [showContactModal, setShowContactModal] = useState(false);
    const [showInstallAppModal, setShowInstallAppModal] = useState(true);
    const [isFromSubscriptionPrompt, setIsFromSubscriptionPrompt] = useState(false);

    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const { t, i18n } = useTranslation();

    const toggleDrawer = (newOpen: boolean) => () => {
        setDrawerOpen(newOpen);
        window.gtag?.('event', `opened drawer - ${newOpen}`, {});
    };

    const [activeTab, setActiveTab] = useState<number>(0);
    const [isMobile, setIsMobile] = useState<boolean>(false);

    const loginUser = async (user: any) => {
        if (!user) return;

        try {
            const response = await login(user);
            const userFromBE: UserType = response.data.user;
            setUser(userFromBE);

            if (userFromBE?.settings?.languageCode) {
                i18n.changeLanguage(userFromBE.settings.languageCode);
            }

            const loggedInTimes = userFromBE?.misc?.loggedInTimes;
            const userIsUnsubscribed: boolean = isUserUnsubscribed(userFromBE);
            if (userIsUnsubscribed && loggedInTimes % 10 === 0) {
                openSubscribe();
            }

            if (loggedInTimes % 14 === 0) {
                setShowContactModal(true);
            }

            // localStorage.setItem('authToken', response.data.token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

            if (response.data?.user?.settings?.userType === 'prime') {
                setIsMobile(true); // prime users can use the app on desktop
            }

            window.gtag?.('config', 'G-BPWT724M06', {
                user_id: userFromBE.id,
                user_type: userFromBE.settings.userType === 'prime' ? 'developer' : 'regular',
                language: userFromBE.settings.languageCode,
            });

            window.gtag?.('event', 'landed', {
                loggedInTimes: userFromBE?.misc?.loggedInTimes,
            });
        } catch (error: any) {
            alert(`Error logging in. ${JSON.stringify(error.response?.data)}`);
            console.error('Error login:', error.response?.data);
        }
    };

    useEffect(() => {
        // TODO: TRY REACTIVE WAY: https://docs.telegram-mini-apps.com/packages/telegram-apps-sdk-react/2-x
        const script = document.createElement('script');
        script.src = 'https://telegram.org/js/telegram-web-app.js';
        script.async = true;
        document.body.appendChild(script);

        script.onload = () => {
            if (window.Telegram && window.Telegram.WebApp) {
                // alert('2 Telegram WebApp initialized!');
                const tg = window.Telegram.WebApp;

                tg.ready();

                TelegramAnalytics.init({
                    token: process.env.REACT_APP_ANALYTICS_TG_TOKEN as string,
                    appName: 'CalPalPro_analytics',
                });

                const platform = tg.platform;
                if (platform === 'android' || platform === 'ios') {
                    // TODO: !! INSTEAD OF THIS TRY TO RETURN DOM IMMEDIATELY TO HIDE LOGIN CALL COMPLETELY !!
                    setIsMobile(true);
                }

                console.log('%c >>> ', 'background: #333; color: #bada55', tg);

                // TODO: review this approach instead of decoding directly from URL OR use npm package
                // console.log('%c >>> ', 'background: #333; color: #bada55', tg.initDataUnsafe);

                tg.disableVerticalSwipes();

                tg.expand(); // expand (not half-screen)
                // @ts-ignore
                // tg.requestFullscreen(); // full screen size - covers camera and disable scrolling

                // @ts-ignore
                tg.lockOrientation();

                // tg.setHeaderColor(CALPAL_THEME.palette.primary.main);
                tg.setHeaderColor(colors.green[800]);
                tg.setBottomBarColor(CALPAL_THEME.palette.primary.main); // this is for android
            }
        };

        return () => {
            document.body.removeChild(script);
        };
    }, []);

    useEffect(() => {
        const { tgData, user } = decodeTelegramUrlParams(window.location.hash.substring(1));
        // setTgData(tgData);

        loginUser(user);
    }, []);

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);

        window.gtag?.('event', `open: ${['Today', 'History', 'Favorites'][newValue]}`, {});
    };

    if (env === 'production' && !isMobile) {
        return (
            // <div className="h-screen flex items-center justify-center">
            //     <div className="flex flex-col items-center">
            //         <img src="/avatar_200x200.png" alt="" className="w-50 h-50 mb-4" />
            //         <div className="text-gray-500">Please open this app on your phone.</div>
            //     </div>
            // </div>
            // todo: this is to try approve the app for adsense
            <iframe
                src="https://www.calpal.pro/"
                style={{
                    border: 'none',
                    display: 'block',
                    margin: '0',
                    padding: '0',
                    width: '100%',
                    height: '100vh',
                }}
                sandbox={[
                    'allow-scripts',
                    'allow-same-origin',
                    'allow-popups',
                    'allow-popups-to-escape-sandbox',
                    'allow-top-navigation-by-user-activation',
                ].join(' ')}
            />
        );
    }

    return (
        <div>
            {!user && (
                <div className="mt-40 text-center">
                    <CircularProgress color="secondary" />
                </div>
            )}

            {user && (
                <>
                    <AppBar position="static">
                        <Toolbar>
                            <div className="text-white">{t('HEADING_title')}</div>
                            <IconButton
                                style={{ marginLeft: 'auto' }}
                                size="large"
                                edge="end"
                                color="inherit"
                                aria-label="menu"
                                onClick={toggleDrawer(true)}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Toolbar>
                    </AppBar>
                    {/*{env !== 'production' && (*/}
                    {/*    <div className="mt-20 text-center">*/}
                    {/*      <button*/}
                    {/*          onClick={() => {*/}
                    {/*            window.open(window.location.href, '_blank');*/}
                    {/*          }}*/}
                    {/*      >*/}
                    {/*        Open in Browser*/}
                    {/*      </button>*/}
                    {/*    </div>*/}
                    {/*)}*/}
                    <Tabs variant="fullWidth" value={activeTab} onChange={handleTabChange}>
                        <Tab icon={<TodayIcon />} label={t('TAB_today')} iconPosition="start" />
                        <Tab icon={<ListIcon />} label={t('TAB_history')} iconPosition="start" />
                        <Tab icon={<FavoriteIcon />} label={t('TAB_favs')} iconPosition="start" />
                    </Tabs>

                    <main className="overflow-y-auto h-[calc(100vh-72px-56px)]">
                        {activeTab === 0 && <HomeTab user={user} />}
                        {activeTab === 1 && <HistoryTab user={user} />}
                        {activeTab === 2 && <FavoritesTab user={user} />}
                    </main>

                    <Drawer open={drawerOpen} anchor="right" onClose={toggleDrawer(false)}>
                        <DrawerContent user={user} close={toggleDrawer(false)} />
                    </Drawer>

                    {showSubscribeModal && (
                        <SubscribeModal
                            isInitial
                            user={user}
                            onCancel={() => {
                                // setShowSubscribeModal(false);
                                closeSubscribe();
                                setIsFromSubscriptionPrompt(true);
                                setShowContactModal(true);
                            }}
                        />
                    )}

                    {showContactModal && (
                        <ContactModal
                            isFromSubscriptionPrompt={isFromSubscriptionPrompt}
                            user={user}
                            onCancel={() => {
                                setShowContactModal(false);
                                setIsFromSubscriptionPrompt(false);
                            }}
                            onSuccess={() => {
                                setShowContactModal(false);
                                setSuccessMessage(t('SUCCESS_MSG_message_sent'));
                            }}
                        />
                    )}

                    {showInstallAppModal && (
                        <InstallAppModal
                            onCancel={() => {
                                setShowInstallAppModal(false);
                            }}
                        />
                    )}

                    {successMessage && (
                        <Snackbar
                            open
                            autoHideDuration={3000}
                            onClose={() => {
                                setSuccessMessage(null);
                            }}
                            message={successMessage}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default App;
